import { useEffect, useState } from "react";
import { format } from "date-fns";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { YStack } from "tamagui";

import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { useAuth, type AuthRole } from "@/contexts/AuthContext";
import {
  AdminInitialAvatar,
  AdminPageScaffold,
  AdminStatusPill,
} from "@/components/AdminScaffold";
import {
  AppButton,
  AppButtonRow,
  AppCard,
  AppCardBody,
  AppEmptyState,
  AppLoadingState,
  AppText,
  kindredFontFamilies,
} from "../../ui-foundation";

type RoleRow = {
  id: string;
  role: AuthRole;
};

type MemberProfile = Pick<
  Database["public"]["Views"]["admin_profiles"]["Row"],
  "id" | "display_name" | "postcode" | "bio" | "avatar_url" | "account_status" | "created_at"
>;

type ModerationLogEntry = Database["public"]["Tables"]["moderation_log"]["Row"] & {
  moderator?: {
    display_name: string | null;
  } | null;
};

const getHighestRole = (roles: AuthRole[]): AuthRole => {
  if (roles.includes("admin")) return "admin";
  if (roles.includes("moderator")) return "moderator";
  return "user";
};

const getRoleSaveErrorMessage = (error: unknown) => {
  const code =
    typeof error === "object" && error && "code" in error ? String((error as { code?: string }).code ?? "") : "";
  const message =
    typeof error === "object" && error && "message" in error
      ? String((error as { message?: string }).message ?? "")
      : "";
  const details =
    typeof error === "object" && error && "details" in error
      ? String((error as { details?: string }).details ?? "")
      : "";
  const combined = `${code} ${message} ${details}`.toLowerCase();

  if (
    combined.includes("aal2") ||
    combined.includes("mfa") ||
    combined.includes("authenticator") ||
    combined.includes("assurance")
  ) {
    return "This action requires a verified admin session. Complete account verification and try again.";
  }

  if (
    code === "42501" ||
    combined.includes("permission denied") ||
    combined.includes("row-level security") ||
    combined.includes("not allowed")
  ) {
    return "You do not have permission to change roles.";
  }

  return "Role could not be saved. Please try again.";
};

const AdminMemberDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const {
    user,
    role: viewerRole,
    mfaLevel,
    requiresMfa,
    requiresPrivilegedMfaSetup,
  } = useAuth();
  const [profile, setProfile] = useState<MemberProfile | null>(null);
  const [roleRows, setRoleRows] = useState<RoleRow[]>([]);
  const [role, setRole] = useState<AuthRole>("user");
  const [newRole, setNewRole] = useState<AuthRole>("user");
  const [memberEmail, setMemberEmail] = useState<string | null>(null);
  const [modLog, setModLog] = useState<ModerationLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingRole, setSavingRole] = useState(false);
  const [deletingMember, setDeletingMember] = useState(false);

  useEffect(() => {
    const fetchMember = async () => {
      const [
        { data: nextProfile, error: profileError },
        { data: nextRoles, error: roleError },
        { data: log, error: logError },
        { data: contactData, error: contactError },
      ] =
        await Promise.all([
          supabase
            .from("admin_profiles")
            .select("id, display_name, postcode, bio, avatar_url, account_status, created_at")
            .eq("id", id!)
            .single(),
          supabase
            .from("user_roles")
            .select("id, role")
            .eq("user_id", id!)
            .order("assigned_at", { ascending: false }),
          supabase
            .from("moderation_log")
            .select("*, moderator:profiles!moderation_log_moderator_id_fkey(display_name)")
            .eq("target_user_id", id!)
            .order("created_at", { ascending: false }),
          supabase.rpc("get_admin_member_contact" as any, {
            target_user_id: id!,
          }),
        ]);

      if (profileError) {
        console.error("Could not load member profile:", profileError);
        toast.error("Could not load member details.");
        setLoading(false);
        return;
      }

      if (roleError) {
        console.error("Could not load member roles:", roleError);
        toast.error("Could not load member role.");
      }

      if (logError) {
        console.error("Could not load moderation log:", logError);
      }

      if (contactError) {
        console.error("Could not load member contact details:", contactError);
      }

      const nextRoleRows = ((nextRoles || []) as RoleRow[]);
      const currentRole = getHighestRole(nextRoleRows.map((entry) => entry.role));

      setProfile(nextProfile);
      setRoleRows(nextRoleRows);
      setRole(currentRole);
      setNewRole(currentRole);
      setMemberEmail((contactData as { email?: string | null } | null)?.email ?? null);
      setModLog((log || []) as ModerationLogEntry[]);
      setLoading(false);
    };

    void fetchMember();
  }, [id]);

  const doAction = async (action: string, newStatus: string) => {
    if (!user) return;

    await Promise.all([
      supabase.from("profiles").update({ account_status: newStatus }).eq("id", id!),
      supabase.from("moderation_log").insert({ target_user_id: id!, action, moderator_id: user.id }),
    ]);

    setProfile((current) => (current ? { ...current, account_status: newStatus } : current));
    setModLog((previous) => [
      { action, created_at: new Date().toISOString(), moderator: { display_name: "You" } },
      ...previous,
    ]);
    toast.success(`Member ${action.toLowerCase()}`);
  };

  const saveRole = async () => {
    if (!id || newRole === role || savingRole) return;

    if (viewerRole !== "admin") {
      toast.error("You do not have permission to change roles.");
      return;
    }

    if (requiresPrivilegedMfaSetup || requiresMfa || mfaLevel !== "aal2") {
      toast.error("This action requires a verified admin session. Complete account verification and try again.");
      return;
    }

    setSavingRole(true);

    try {
      const { data: currentRoleRows, error: loadError } = await supabase
        .from("user_roles")
        .select("id, role")
        .eq("user_id", id)
        .order("assigned_at", { ascending: false });

      if (loadError) {
        throw loadError;
      }

      const existingRoleRows = (((currentRoleRows && currentRoleRows.length > 0) ? currentRoleRows : roleRows) ||
        []) as RoleRow[];
      const matchingRow = existingRoleRows.find((entry) => entry.role === newRole);
      const primaryRowId = matchingRow?.id ?? existingRoleRows[0]?.id ?? null;

      if (!primaryRowId) {
        const { error: insertError } = await supabase.from("user_roles").insert({ user_id: id, role: newRole });
        if (insertError) throw insertError;
      } else {
        if (!matchingRow) {
          const { error: updateError } = await supabase
            .from("user_roles")
            .update({ role: newRole })
            .eq("id", primaryRowId);
          if (updateError) throw updateError;
        }

        const staleRoleIds = existingRoleRows.filter((entry) => entry.id !== primaryRowId).map((entry) => entry.id);
        if (staleRoleIds.length > 0) {
          const { error: deleteError } = await supabase.from("user_roles").delete().in("id", staleRoleIds);
          if (deleteError) throw deleteError;
        }
      }

      const { data: savedRoleRows, error: refreshError } = await supabase
        .from("user_roles")
        .select("id, role")
        .eq("user_id", id)
        .order("assigned_at", { ascending: false });

      if (refreshError) throw refreshError;

      const nextRoleRows = ((savedRoleRows || []) as RoleRow[]);
      const nextRole = getHighestRole(nextRoleRows.map((entry) => entry.role));

      if (!nextRoleRows.length || nextRole !== newRole) {
        throw new Error("Role could not be saved. Please try again.");
      }

      setRoleRows(nextRoleRows);
      setRole(nextRole);
      setNewRole(nextRole);
      toast.success("Role updated");
    } catch (error) {
      console.error("Could not save role:", error);
      toast.error(getRoleSaveErrorMessage(error));
    } finally {
      setSavingRole(false);
    }
  };

  const deleteMember = async () => {
    if (!id || deletingMember) return;

    if (!window.confirm("Delete this member profile and all linked data? This cannot be undone.")) {
      return;
    }

    setDeletingMember(true);

    const { error } = await supabase.rpc("admin_delete_member" as any, {
      target_user_id: id,
      note_text: null,
    });

    setDeletingMember(false);

    if (error) {
      console.error("Could not delete member:", error);
      toast.error("Member could not be deleted. Please try again.");
      return;
    }

    toast.success("Member deleted");
    navigate("/admin/members");
  };

  if (loading) {
    return (
      <AdminPageScaffold title="Member detail" subtitle="Loading member profile and moderation history." fallbackPath="/admin/members">
        <AppLoadingState
          title="Loading member detail"
          body="Pulling role history, profile status, and moderation actions."
        />
      </AdminPageScaffold>
    );
  }

  if (!profile) {
    return (
      <AdminPageScaffold title="Member detail" subtitle="This member record could not be loaded." fallbackPath="/admin/members">
        <AppEmptyState
          title="Member not found"
          body="The requested member record is missing or no longer available."
          action={
            <AppButton type="button" tone="outline" onPress={() => navigate("/admin/members")}>
              Back to members
            </AppButton>
          }
        />
      </AdminPageScaffold>
    );
  }

  const warningCount = modLog.filter((entry) => entry.action === "warning").length;
  const roleLabel = role.charAt(0).toUpperCase() + role.slice(1);
  const accountTone = profile.account_status === "active" ? "success" : profile.account_status === "suspended" ? "warning" : "danger";

  return (
    <AdminPageScaffold
      title={profile.display_name || "Member detail"}
      titleLines={2}
      subtitle="Review account status, role access, and moderation history for this member."
      fallbackPath="/admin/members"
    >
      <AppCard>
        <AppCardBody alignItems="center">
          <AdminInitialAvatar name={profile.display_name} size={72} />
          <YStack alignItems="center" gap={4}>
            <AppText fontFamily={kindredFontFamilies.sansBold} fontSize="$xl" lineHeight="$xl">
              {profile.display_name || "No name"}
            </AppText>
            {profile.postcode ? (
              <AppText color="$colorMuted" fontSize="$sm" lineHeight="$sm">
                {profile.postcode}
              </AppText>
            ) : null}
            {memberEmail ? (
              <AppText color="$colorMuted" fontSize="$sm" lineHeight="$sm">
                {memberEmail}
              </AppText>
            ) : null}
            <AppText color="$colorMuted" fontSize="$sm" lineHeight="$sm">
              Joined {format(new Date(profile.created_at), "MMMM yyyy")}
            </AppText>
          </YStack>
          <AdminStatusPill tone={role === "admin" ? "primary" : role === "moderator" ? "secondary" : "neutral"}>
            {roleLabel}
          </AdminStatusPill>
        </AppCardBody>
      </AppCard>

      <AppCard>
        <AppCardBody>
          <YStack gap="$sm">
            <YStack gap={6}>
              <AppText fontFamily={kindredFontFamilies.sansBold} fontSize="$lg" lineHeight="$lg">
                Account status
              </AppText>
              <AppText color="$colorMuted" fontSize="$sm" lineHeight="$sm">
                Change member access without leaving the shared admin flow.
              </AppText>
            </YStack>
            <AdminStatusPill tone={accountTone}>
              {profile.account_status.charAt(0).toUpperCase() + profile.account_status.slice(1)}
            </AdminStatusPill>
            {warningCount > 0 ? (
              <AppText color="$colorMuted" fontSize="$xs" lineHeight="$sm">
                {warningCount} warning{warningCount > 1 ? "s" : ""} issued
              </AppText>
            ) : null}
            <AppButtonRow>
              {profile.account_status === "active" ? (
                <>
                  <AppButton type="button" tone="outline" size="sm" onPress={() => doAction("warning", "active")}>
                    Issue warning
                  </AppButton>
                  <AppButton type="button" tone="danger" size="sm" onPress={() => doAction("suspended", "suspended")}>
                    Suspend
                  </AppButton>
                </>
              ) : null}
              {profile.account_status === "suspended" ? (
                <>
                  <AppButton type="button" tone="secondary" size="sm" onPress={() => doAction("restored", "active")}>
                    Restore access
                  </AppButton>
                  <AppButton type="button" tone="danger" size="sm" onPress={() => doAction("banned", "banned")}>
                    Ban permanently
                  </AppButton>
                </>
              ) : null}
              {profile.account_status === "banned" ? (
                <AppButton type="button" tone="secondary" size="sm" onPress={() => doAction("restored", "active")}>
                  Restore access
                </AppButton>
              ) : null}
              <AppButton type="button" tone="danger" size="sm" onPress={() => void deleteMember()} disabled={deletingMember}>
                {deletingMember ? "Deleting..." : "Delete profile"}
              </AppButton>
            </AppButtonRow>
          </YStack>
        </AppCardBody>
      </AppCard>

      <AppCard>
        <AppCardBody>
          <YStack gap="$sm">
            <YStack gap={6}>
              <AppText fontFamily={kindredFontFamilies.sansBold} fontSize="$lg" lineHeight="$lg">
                Change role
              </AppText>
              <AppText color="$colorMuted" fontSize="$sm" lineHeight="$sm">
                This still requires a verified admin session with the right MFA level.
              </AppText>
            </YStack>
            <AppButtonRow>
              {(["user", "moderator", "admin"] as AuthRole[]).map((candidate) => (
                <AppButton
                  key={candidate}
                  type="button"
                  tone={newRole === candidate ? "secondary" : "outline"}
                  size="sm"
                  onPress={() => setNewRole(candidate)}
                >
                  {candidate === "user" ? "Member" : candidate.charAt(0).toUpperCase() + candidate.slice(1)}
                </AppButton>
              ))}
            </AppButtonRow>
            <AppButton
              type="button"
              size="sm"
              alignSelf="flex-start"
              onPress={saveRole}
              disabled={newRole === role || savingRole}
            >
              {savingRole ? "Saving..." : "Save role"}
            </AppButton>
          </YStack>
        </AppCardBody>
      </AppCard>

      <YStack gap="$sm">
        <YStack gap={6}>
          <AppText fontFamily={kindredFontFamilies.sansBold} fontSize="$lg" lineHeight="$lg">
            Moderation log
          </AppText>
          <AppText color="$colorMuted" fontSize="$sm" lineHeight="$sm">
            Recorded actions taken against this member.
          </AppText>
        </YStack>
        {modLog.length === 0 ? (
          <AppEmptyState
            title="No actions taken"
            body="Warnings, suspensions, and moderator notes will appear here."
          />
        ) : (
          <YStack gap="$sm">
            {modLog.map((entry, index) => (
              <AppCard key={`${entry.created_at}-${index}`}>
                <AppCardBody>
                  <AppText fontFamily={kindredFontFamilies.sansBold} fontSize="$sm" lineHeight="$sm" textTransform="capitalize">
                    {entry.action}
                  </AppText>
                  <AppText color="$colorMuted" fontSize="$xs" lineHeight="$sm">
                    {format(new Date(entry.created_at), "d MMM yyyy, HH:mm")} · by{" "}
                    {entry.moderator?.display_name || "Unknown"}
                  </AppText>
                  {entry.notes ? (
                    <AppText color="$colorMuted" fontSize="$sm" lineHeight="$md">
                      {entry.notes}
                    </AppText>
                  ) : null}
                </AppCardBody>
              </AppCard>
            ))}
          </YStack>
        )}
      </YStack>
    </AdminPageScaffold>
  );
};

export default AdminMemberDetail;
